import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const PRODUCT_TITLES = {
  "standard-visit": "(Combo) Science Centre + Energy Capsule",
  "evening-xperience": "Science Centre Visit",
  "priority-access": "Energy Capsule Ride",
  "hop-on-hop-off": "(Combo) Science Centre + Storibox Photo",
};

function readStored(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function parseDate(value) {
  if (typeof value !== "string") return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );

  return date.getFullYear() === Number(match[1]) &&
    date.getMonth() === Number(match[2]) - 1 &&
    date.getDate() === Number(match[3])
    ? date
    : null;
}

function formatDate(value) {
  const date = parseDate(value);

  return date
    ? date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";
}

function formatTime(value) {
  if (typeof value !== "string") return "";

  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return "";

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (hour > 23 || minute > 59) return "";

  return `${String(hour % 12 || 12).padStart(2, "0")}:${String(
    minute
  ).padStart(2, "0")} ${hour >= 12 ? "PM" : "AM"}`;
}

function money(value) {
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function normalizeTickets(value) {
  if (!Array.isArray(value)) return [];

  return value
    .filter((ticket) => ticket && typeof ticket === "object")
    .map((ticket, index) => ({
      ...ticket,
      id: ticket.id || `cart-ticket-${index}`,
      quantity: Math.floor(Number(ticket.quantity)),
      price: Number(ticket.price),
    }))
    .filter(
      (ticket) =>
        Number.isFinite(ticket.quantity) &&
        ticket.quantity > 0 &&
        Number.isFinite(ticket.price) &&
        ticket.price >= 0
    );
}

function getCartTotals(tickets) {
  return {
    totalQuantity: tickets.reduce(
      (sum, ticket) => sum + ticket.quantity,
      0
    ),
    total:
      tickets.reduce(
        (sum, ticket) =>
          sum + Math.round(ticket.price * 100) * ticket.quantity,
        0
      ) / 100,
  };
}

function getProductTitle(ticket) {
  return (
    PRODUCT_TITLES[ticket.attractionId] ||
    ticket.title ||
    "Petrosains"
  );
}

// Данные последнего booking используются только для его билетов.
function getMatchingBooking(ticket, booking) {
  if (!booking) return null;

  if (
    ticket.attractionId &&
    ticket.attractionId !== booking.attraction?.id
  ) {
    return null;
  }

  if (
    ticket.visitDate &&
    booking.visitDate &&
    ticket.visitDate !== booking.visitDate
  ) {
    return null;
  }

  if (
    ticket.residentType &&
    booking.residentType &&
    ticket.residentType !== booking.residentType
  ) {
    return null;
  }

  if (
    ticket.timeSlot &&
    booking.timeSlot &&
    ticket.timeSlot !== booking.timeSlot
  ) {
    return null;
  }

  if (
    ticket.timeSlots?.energy &&
    booking.timeSlots?.energy &&
    ticket.timeSlots.energy !== booking.timeSlots.energy
  ) {
    return null;
  }

  return booking;
}

function getStatusLabel(ticket, booking) {
  const status = ticket.dateStatus || booking?.dateStatus;

  if (status === "peak") return "Peak";
  if (status === "offPeak") return "Non-Peak";
  if (status === "publicHoliday") return "Public Holiday";
  if (status === "closed") return "Closed";

  const date = parseDate(ticket.visitDate || booking?.visitDate);

  return date && [0, 6].includes(date.getDay())
    ? "Peak"
    : "Non-Peak";
}

function getResidentLabel(ticket, booking) {
  return (ticket.residentType || booking?.residentType) === "mykad"
    ? "MyKad"
    : "Non-MyKad";
}

function getTicketSessions(ticket, booking) {
  const visitDate = ticket.visitDate || booking?.visitDate;

  if (ticket.attractionId === "standard-visit") {
    return [
      {
        id: "science",
        title: "Science Centre Visit",
        date: ticket.scienceCentreDate || visitDate,
        time:
          ticket.timeSlots?.science ||
          ticket.scienceCentreTimeSlot ||
          ticket.timeSlot ||
          booking?.timeSlots?.science ||
          booking?.scienceCentreTimeSlot ||
          booking?.timeSlot,
      },
      {
        id: "energy",
        title: "Energy Capsule Ride",
        date: ticket.energyCapsuleDate || visitDate,
        time:
          ticket.timeSlots?.energy ||
          ticket.energyCapsuleTimeSlot ||
          booking?.timeSlots?.energy ||
          booking?.energyCapsuleTimeSlot,
      },
    ];
  }

  return [
    {
      id: "visit",
      title:
        ticket.attractionId === "hop-on-hop-off"
          ? "Science Centre Visit"
          : getProductTitle(ticket),
      date: visitDate,
      time: ticket.timeSlot || booking?.timeSlot,
    },
  ];
}

function updateBooking(booking, tickets) {
  const totals = getCartTotals(tickets);

  return {
    ...(booking || {}),
    tickets,
    total: totals.total,
    totalQuantity: totals.totalQuantity,
    currency: "MYR",
  };
}

function TrashIcon() {
  return (
    <svg
      width="17"
      height="19"
      viewBox="0 0 20 22"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3.5 5.5h13M7.5 5.5V3h5v2.5M5 5.5l.8 14h8.4l.8-14M8 8.5v8M10 8.5v8M12 8.5v8"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Booking({
  nextPath = "/form",
  backPath = "/time",
  onNext,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(
    () => location.state?.booking || readStored("visitBooking")
  );

  const [tickets, setTickets] = useState(() => {
    const stored = readStored("selectedTickets");

    return normalizeTickets(
      Array.isArray(stored)
        ? stored
        : location.state?.selectedTickets ||
            location.state?.tickets ||
            booking?.tickets ||
            []
    );
  });

  const [error, setError] = useState("");

  const { totalQuantity, total } = useMemo(
    () => getCartTotals(tickets),
    [tickets]
  );

  function removeTicket(id) {
    const nextTickets = tickets.filter((ticket) => ticket.id !== id);
    const nextBooking = updateBooking(booking, nextTickets);

    const nextCheckout = {
      tickets: nextTickets,
      ...getCartTotals(nextTickets),
      currency: "MYR",
    };

    try {
      localStorage.setItem(
        "selectedTickets",
        JSON.stringify(nextTickets)
      );

      localStorage.setItem(
        "visitBooking",
        JSON.stringify(nextBooking)
      );

      localStorage.setItem(
        "bookingCheckout",
        JSON.stringify(nextCheckout)
      );
    } catch {
      setError("Unable to update your cart. Please try again.");
      return;
    }

    setTickets(nextTickets);
    setBooking(nextBooking);
    setError("");

    window.dispatchEvent(new Event("cartUpdated"));
  }

  function handleBack() {
    const params = new URLSearchParams();

    const attractionId =
      booking?.attraction?.id || tickets[0]?.attractionId;

    if (attractionId) {
      params.set("attraction", attractionId);
    }

    const currentBooking = updateBooking(booking, tickets);

    navigate(
      {
        pathname: backPath,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      {
        state: {
          booking: currentBooking,
          tickets,
          selectedTickets: tickets,
        },
      }
    );

    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }

  function handleNext() {
    if (!tickets.length) return;

    // Старый билет комбо без второго времени нужно выбрать заново.
    const incompleteTicket = tickets.find((ticket) => {
      const relatedBooking = getMatchingBooking(ticket, booking);
      const sessions = getTicketSessions(ticket, relatedBooking);

      return sessions.some(
        (session) =>
          !parseDate(session.date) || !formatTime(session.time)
      );
    });

    if (incompleteTicket) {
      setError(
        `Please select the visit date and all time slots for ${getProductTitle(
          incompleteTicket
        )}.`
      );
      return;
    }

    const currentBooking = updateBooking(booking, tickets);

    const checkout = {
      tickets,
      total,
      totalQuantity,
      currency: "MYR",
    };

    try {
      localStorage.setItem(
        "selectedTickets",
        JSON.stringify(tickets)
      );

      localStorage.setItem(
        "visitBooking",
        JSON.stringify(currentBooking)
      );

      localStorage.setItem(
        "bookingCheckout",
        JSON.stringify(checkout)
      );
    } catch {
      setError("Unable to save your booking. Please try again.");
      return;
    }

    setBooking(currentBooking);
    setError("");

    const state = {
      booking: currentBooking,
      tickets,
      selectedTickets: tickets,
      checkout,
    };

    if (onNext) {
      onNext(state);
      return;
    }

    navigate(nextPath, { state });

    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }

  return (
    <main className="booking-page">
      <style>{CSS}</style>

      <div className="booking-container">
        <div className="booking-topbar">
          <h1>My Cart</h1>

          <Link to="/" className="booking-home">
            Back To Home
          </Link>
        </div>

        <div className="booking-columns">
          <section
            className="booking-cart"
            aria-label="Selected tickets"
          >
            {tickets.length ? (
              tickets.map((ticket) => {
                const relatedBooking = getMatchingBooking(
                  ticket,
                  booking
                );

                const resident = getResidentLabel(
                  ticket,
                  relatedBooking
                );

                const status = getStatusLabel(
                  ticket,
                  relatedBooking
                );

                const ticketLabel = (
                  ticket.ticketLabel ||
                  ticket.ticketType ||
                  "Adult"
                ).toUpperCase();

                const sessions = getTicketSessions(
                  ticket,
                  relatedBooking
                );

                return (
                  <article
                    className="booking-item"
                    key={ticket.id}
                  >
                    <h2>
                      {getProductTitle(ticket)} – {resident} ({status})
                    </h2>

                    <div className="booking-detail">
                      <strong>Quantity:</strong>

                      <p>
                        {ticket.quantity} X {ticketLabel}
                      </p>
                    </div>

                    {sessions.map((session) => {
                      const date = formatDate(session.date);
                      const time = formatTime(session.time);

                      return (
                        <div
                          className="booking-detail booking-session"
                          key={session.id}
                        >
                          <strong>
                            {session.title} – {resident} ({status}) (
                            {ticketLabel}):
                          </strong>

                          <p>
                            {date || "Date not selected"}
                            {", "}
                            {time || "Time not selected"}
                          </p>
                        </div>
                      );
                    })}

                    <div className="booking-item-bottom">
                      <button
                        type="button"
                        className="booking-remove"
                        aria-label={`Remove ${ticketLabel} ticket for ${getProductTitle(
                          ticket
                        )}`}
                        onClick={() => removeTicket(ticket.id)}
                      >
                        <TrashIcon />
                      </button>

                      <span>
                        MYR {money(ticket.price * ticket.quantity)}
                      </span>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="booking-empty">
                <h2>Your cart is empty</h2>

                <p>Select your tickets to continue.</p>

                <Link to="/">Browse Attractions</Link>
              </div>
            )}
          </section>

          <aside
            className="booking-summary"
            aria-label="Order summary"
          >
            <h2>Summary</h2>

            <div className="booking-summary-body">
              <div className="booking-summary-row">
                <span>Sub Total ({totalQuantity} items)</span>

                <span>MYR {money(total)}</span>
              </div>

              <div className="booking-summary-row booking-total">
                <strong>Total</strong>

                <strong>MYR {money(total)}</strong>
              </div>
            </div>
          </aside>
        </div>

        {error && (
          <p className="booking-error" role="alert">
            {error}
          </p>
        )}

        <div className="booking-actions">
          <button
            type="button"
            className="booking-back"
            onClick={handleBack}
          >
            Back
          </button>

          <button
            type="button"
            className="booking-next"
            disabled={!tickets.length}
            onClick={handleNext}
          >
            Next Step
          </button>
        </div>
      </div>
    </main>
  );
}

const CSS = `
.booking-page {
  min-height: 670px;
  padding: 21px 24px 58px;
  background: #fff;
  color: #050505;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
}

.booking-page * {
  box-sizing: border-box;
}

.booking-page button,
.booking-page a {
  -webkit-tap-highlight-color: transparent;
}

.booking-page button {
  font: inherit;
  cursor: pointer;
}

.booking-page button:focus-visible,
.booking-page a:focus-visible {
  outline: 3px solid #007d69;
  outline-offset: 4px;
}

.booking-container {
  width: 100%;
  max-width: 1248px;
  margin: 0 auto;
}

.booking-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 41px;
  margin-bottom: 27px;
}

.booking-topbar h1 {
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  line-height: 41px;
  letter-spacing: -.4px;
}

.booking-home {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 144px;
  height: 41px;
  padding: 0 13px;
  border: 2px solid #b0b0b0;
  border-radius: 6px;
  background: #fff;
  color: #aaa;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  text-decoration: none;
  white-space: nowrap;
}

.booking-home:hover {
  border-color: #888;
  color: #888;
}

.booking-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
  gap: 34px;
}

.booking-cart,
.booking-summary {
  min-width: 0;
  border-radius: 5px;
  background: #fff;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, .08),
    0 4px 30px rgba(0, 0, 0, .07);
}

.booking-cart {
  padding: 24px 19px 36px;
}

.booking-item {
  padding: 0 9px;
  border-bottom: 1px solid #dce0e3;
}

.booking-item + .booking-item {
  padding-top: 25px;
}

.booking-item h2 {
  margin: 0 0 13px;
  font-size: 16px;
  font-weight: 700;
  line-height: 23px;
  overflow-wrap: anywhere;
}

.booking-detail {
  margin: 0 0 9px;
  font-size: 16px;
  line-height: 24px;
}

.booking-detail strong {
  display: block;
  font-weight: 600;
  line-height: 23px;
}

.booking-detail p {
  margin: 2px 0 0;
  line-height: 24px;
}

.booking-session {
  margin-top: 9px;
}

.booking-item-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 57px;
  margin: 13px -3px 0;
  padding: 0 0 16px;
  font-size: 16px;
  line-height: 24px;
}

.booking-item-bottom > span {
  white-space: nowrap;
}

.booking-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #00b6b3;
}

.booking-remove:hover {
  background: #effaf8;
  color: #009f91;
}

.booking-summary {
  border-radius: 0;
}

.booking-summary > h2 {
  display: flex;
  align-items: center;
  min-height: 51px;
  margin: 0;
  padding: 0 22px;
  border-bottom: 1px solid #ddd;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.booking-summary-body {
  padding: 26px 22px 28px;
}

.booking-summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 26px;
  font-size: 16px;
  line-height: 25px;
}

.booking-summary-row > :last-child {
  white-space: nowrap;
}

.booking-total {
  margin-top: 26px;
  font-size: 18px;
  line-height: 27px;
}

.booking-total strong {
  font-weight: 700;
}

.booking-actions {
  display: grid;
  grid-template-columns: 287px 393px;
  justify-content: end;
  gap: 34px;
  margin-top: 128px;
}

.booking-actions button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: 0 20px;
  border: 0;
  border-radius: 6px;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 23px;
}

.booking-back {
  background: #f5cc48;
}

.booking-back:hover {
  background: #eec13b;
}

.booking-next {
  background: #00ab91;
}

.booking-next:hover:not(:disabled) {
  background: #009d84;
}

.booking-next:disabled {
  opacity: .45;
  cursor: not-allowed;
}

.booking-error {
  margin: 20px 0 0;
  color: #c53333;
  font-size: 14px;
}

.booking-empty {
  padding: 26px 10px;
  text-align: center;
}

.booking-empty h2 {
  margin: 0 0 10px;
  font-size: 21px;
  font-weight: 700;
}

.booking-empty p {
  margin: 0 0 22px;
  color: #666;
}

.booking-empty a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0 18px;
  border-radius: 6px;
  background: #00ab91;
  color: #fff;
  font-weight: 600;
  text-decoration: none;
}

@media (min-width: 1024px) {
  .booking-page {
    min-height: 550px;
    padding: 18px 24px 44px;
    font-size: 14px;
    line-height: 22px;
  }

  .booking-container {
    max-width: 1120px;
  }

  .booking-topbar {
    gap: 16px;
    min-height: 36px;
    margin-bottom: 22px;
  }

  .booking-topbar h1 {
    font-size: 26px;
    line-height: 36px;
    letter-spacing: -.3px;
  }

  .booking-home {
    min-width: 126px;
    height: 36px;
    padding: 0 11px;
    font-size: 14px;
    line-height: 20px;
  }

  .booking-columns {
    gap: 28px;
  }

  .booking-cart {
    padding: 20px 16px 28px;
  }

  .booking-item {
    padding: 0 7px;
  }

  .booking-item + .booking-item {
    padding-top: 21px;
  }

  .booking-item h2 {
    margin-bottom: 10px;
    font-size: 14px;
    line-height: 21px;
  }

  .booking-detail {
    margin-bottom: 7px;
    font-size: 14px;
    line-height: 21px;
  }

  .booking-detail strong {
    line-height: 21px;
  }

  .booking-detail p {
    margin-top: 1px;
    line-height: 21px;
  }

  .booking-session {
    margin-top: 7px;
  }

  .booking-item-bottom {
    gap: 16px;
    min-height: 48px;
    margin-top: 10px;
    padding-bottom: 12px;
    font-size: 14px;
    line-height: 21px;
  }

  .booking-remove {
    width: 28px;
    height: 28px;
  }

  .booking-remove svg {
    width: 15px;
    height: 17px;
  }

  .booking-summary > h2 {
    min-height: 44px;
    padding: 0 19px;
    font-size: 14px;
    line-height: 22px;
  }

  .booking-summary-body {
    padding: 21px 19px 23px;
  }

  .booking-summary-row {
    gap: 16px;
    min-height: 23px;
    font-size: 14px;
    line-height: 22px;
  }

  .booking-total {
    margin-top: 21px;
    font-size: 16px;
    line-height: 24px;
  }

  .booking-actions {
    grid-template-columns: 250px 340px;
    gap: 28px;
    margin-top: 96px;
  }

  .booking-actions button {
    height: 43px;
    padding: 0 17px;
    font-size: 14px;
    line-height: 21px;
  }

  .booking-error {
    margin-top: 16px;
    font-size: 12px;
  }

  .booking-empty {
    padding: 22px 9px;
  }

  .booking-empty h2 {
    margin-bottom: 8px;
    font-size: 18px;
  }

  .booking-empty p {
    margin-bottom: 18px;
    font-size: 14px;
  }

  .booking-empty a {
    min-height: 36px;
    padding: 0 15px;
    font-size: 14px;
  }
}

@media (max-width: 900px) {
  .booking-columns {
    gap: 24px;
  }

  .booking-actions {
    grid-template-columns: minmax(180px, 287px) minmax(220px, 393px);
    gap: 24px;
  }

  .booking-cart {
    padding-right: 15px;
    padding-left: 15px;
  }

  .booking-summary-body {
    padding-right: 18px;
    padding-left: 18px;
  }
}

@media (max-width: 640px) {
  .booking-page {
    min-height: 0;
    padding: 20px 16px 36px;
  }

  .booking-topbar {
    gap: 14px;
    margin-bottom: 22px;
  }

  .booking-topbar h1 {
    font-size: 26px;
    line-height: 36px;
  }

  .booking-home {
    min-width: 120px;
    height: 38px;
    padding: 0 10px;
    font-size: 13px;
  }

  .booking-columns {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }

  .booking-cart {
    padding: 21px 15px 25px;
  }

  .booking-item {
    padding-right: 3px;
    padding-left: 3px;
  }

  .booking-item h2 {
    font-size: 15px;
    line-height: 23px;
  }

  .booking-detail,
  .booking-item-bottom {
    font-size: 14px;
  }

  .booking-detail strong,
  .booking-detail p {
    line-height: 22px;
  }

  .booking-summary > h2 {
    min-height: 49px;
    padding: 0 18px;
  }

  .booking-summary-body {
    padding: 21px 18px 24px;
  }

  .booking-summary-row {
    font-size: 14px;
  }

  .booking-total {
    margin-top: 20px;
    font-size: 17px;
  }

  .booking-actions {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.35fr);
    gap: 14px;
    margin-top: 44px;
  }

  .booking-actions button {
    height: 48px;
    font-size: 14px;
  }
}
`;